import { createContext, useContext, ReactNode, useMemo } from 'react'
import { authService } from '@nyos/shared'
import { useScope } from '@nyos/core-framework'
import type { Scope } from '@nyos/core-framework'

interface YardContextType {
  activeFacilityId: string
  switchFacility: (facilityId: string) => void
}

const YardContext = createContext<YardContextType | undefined>(undefined)

const DEFAULT_FACILITY_ID = ''

const getInitialFacilityId = (): string => {
  const user = authService.getUser()
  return user?.facilityId || DEFAULT_FACILITY_ID
}

export const YardProvider = ({ children }: { children: ReactNode }) => {
  const { activeScope, availableScopes, setActiveScope } = useScope()

  // Single source of truth: the rendered facility IS the active scope's
  // facility (falling back to the JWT claim only until a scope exists).
  // No shadow state — apiClient reads nyos_active_scope for X-Facility-ID,
  // so a local override would desynchronize UI and wire scope (ADR-001).
  const activeFacilityId = activeScope?.facilityId || getInitialFacilityId()

  const switchFacility = (facilityId: string) => {
    const scope = availableScopes?.find(s => s.facilityId === facilityId)
    if (scope) {
      setActiveScope(scope)
      return
    }
    // Demo/scenario facilities not in the scope list: synthesize a scope
    // inheriting the current scope's tenant/entity so the pick still flows
    // through ScopeProvider — localStorage and X-Facility-ID follow the UI.
    if (activeScope) {
      setActiveScope({ ...activeScope, facilityId, name: undefined } as Scope)
    }
  }

  const value = useMemo(() => ({ activeFacilityId, switchFacility }), [activeFacilityId, availableScopes])

  return (
    <YardContext.Provider value={value}>
      {children}
    </YardContext.Provider>
  )
}

export const useYard = () => {
  const context = useContext(YardContext)
  if (context === undefined) {
    throw new Error('useYard must be used within a YardProvider')
  }
  return context
}
